<docs>
A dialog layered over the page, blocking the content beneath it (Reka Dialog / AlertDialog). The
header holds the `title` (or `#title`) and the ×; the body an optional tone icon + `description`
row, then the default slot, scrolling; the footer a tertiary action on the left and the
cancel/confirm pair on the right, rendered only when something is put in it. ZonePopover is the
small panel anchored to a trigger that does not block the page; ZoneAlert the inline notice;
ZoneGlobalAlert the page-topping band.

## v-model

Optional, and about the open state (Boolean). Leave it off and the modal owns its own state
(`defaultOpen` sets the initial value), the `#trigger`-slot form. Pass it, even as `false`, and the
modal is fully controlled: the trigger only emits, and nothing opens or closes until you write the
prop back. Same contract as ZonePopover.

## Notes

- The modal teleports to `#zone-teleport` (carries `.w-tw`), so it escapes any overflow/clipping
  ancestor. In tests the zone spec setup stubs Teleport to render in place, so
  `wrapper.find('[role="dialog"]')` works; in the app, query `document.body`. Content only exists
  while open: assert on the trigger, or open it first.
- No z-index on the overlay beyond the `z-50` every teleported Zone overlay uses, so paint order
  inside `#zone-teleport` follows mount order. That is what lets a ZonePopover or ZoneSelect opened
  from inside a modal render above it, and a second modal render above the first.
- The title is always rendered as an `<h2>`: visibly when `title`/`#title` is set, `sr-only` with
  `ariaLabel` otherwise. Reka needs it for `aria-labelledby` and warns to the console without one;
  the zone spec setup turns that warning into a test failure. `aria-describedby` is dropped
  entirely when there is no description.
- The footer stacks on mobile with the primary on top (`flex-col-reverse`), which is why you write
  cancel-then-confirm in source order and get confirm on the right on desktop. Buttons go full width
  on mobile.
- Reka handles the dialog semantics: focus moves in on open and back to the trigger on close, focus
  is trapped, the page behind is inert and scroll-locked, and the rest of the document is hidden
  from screen readers.
- A click outside the dialog never closes it, as Ruby Zone::Modal (a native `<dialog>`) behaves: a
  stray click on the scrim must not throw away what was typed. Escape, the × and the buttons do.
- Sizes come from Figma (410/670/1200) and deliberately differ from Ruby Zone::Modal's 320/640/750.
  Match the Vue numbers when porting a page, not the Ruby ones.

Figma: [Modal](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=973-29033)
</docs>

<template>
  <component
    :is="rootComponent"
    :open="isOpen"
    @update:open="onOpenChange"
  >
    <DialogTrigger
      v-if="$slots.trigger"
      as-child
    >
      <slot
        name="trigger"
        :open="isOpen"
      />
    </DialogTrigger>

    <!--
      Target `#zone-teleport` (defined in the app layouts, see application.scss) rather than the
      reka-ui default of `document.body`. It already carries `.w-tw` — required for Tailwind
      utilities to apply at all, since `important: '.w-tw'` in tailwind.config.js makes `.w-tw` a
      required ancestor — plus the fixed positioning and z-index every other piece of floating Zone
      UI in this app relies on.
    -->
    <DialogPortal to="#zone-teleport">
      <!--
        The overlay doubles as the scroll container and the positioner: the content lives INSIDE it,
        centred by an inner `min-h-full` flex row. That is what makes a taller-than-viewport modal
        scroll the whole overlay instead of being clipped, and it keeps a click anywhere on the
        scrim "outside" the content as far as Reka's DismissableLayer is concerned.
      -->
      <DialogOverlay
        :class="OVERLAY_CLASSES"
        data-testid="modal-overlay"
      >
        <div class="flex min-h-full items-center justify-center p-4 md:p-8">
          <component
            :is="contentComponent"
            v-bind="contentAttrs"
            :class="contentClasses"
            :style="contentStyle"
            @escape-key-down="onEscape"
            @interact-outside.prevent
          >
            <!--
              `contents` rather than `v-if` on the row: the <h2> has to exist either way (Reka
              needs it for aria-labelledby, and warns to the console without one), so when there is
              nothing visible to show it stays in the tree as an `sr-only` title with no header
              chrome around it.
            -->
            <div :class="hasHeader ? HEADER_CLASSES : 'contents'">
              <DialogTitle
                :class="titleClasses"
                data-testid="modal-title"
              >
                <slot name="title">{{ titleText }}</slot>
              </DialogTitle>

              <DialogClose
                v-if="closeButton"
                as-child
              >
                <ZoneIconButton
                  icon="close"
                  size="sm"
                  variant="ghost"
                  color="gray"
                  class="ml-auto"
                  :aria-label="t('close')"
                  data-testid="modal-close"
                />
              </DialogClose>
            </div>

            <div
              :class="bodyClasses"
              data-testid="modal-body"
            >
              <div
                v-if="tone || hasDescription"
                class="flex items-start gap-4"
              >
                <div
                  v-if="tone"
                  :class="iconShapeClasses"
                  aria-hidden="true"
                  data-testid="modal-icon"
                >
                  <ZoneIcon
                    :icon="resolvedIcon"
                    size="lg"
                    :color="toneIcon.color"
                    :shade="toneIcon.shade"
                  />
                </div>

                <DialogDescription
                  v-if="hasDescription"
                  class="min-w-0 flex-1 text-md text-gray-700"
                  data-testid="modal-description"
                >
                  <slot name="description">{{ description }}</slot>
                </DialogDescription>
              </div>

              <slot :close="close" />
            </div>

            <div
              v-if="hasFooter"
              :class="FOOTER_CLASSES"
              data-testid="modal-footer"
            >
              <slot
                name="footer"
                :close="close"
              >
                <div
                  v-if="$slots.tertiary"
                  :class="ACTION_GROUP_CLASSES"
                >
                  <slot
                    name="tertiary"
                    :close="close"
                  />
                </div>

                <div
                  v-if="hasActions"
                  :class="`${ACTION_GROUP_CLASSES} md:ml-auto`"
                >
                  <slot
                    name="actions"
                    :close="close"
                  >
                    <!--
                      In alert mode this MUST be AlertDialogCancel: it is the element Reka focuses
                      on open (AlertDialogContent tracks it through its own context), so an alert
                      dialog without one opens with nothing focused.
                    -->
                    <component
                      :is="cancelComponent"
                      v-if="cancelLabel"
                      as-child
                    >
                      <ZoneButton
                        variant="outline"
                        color="gray_alt"
                        data-testid="modal-cancel"
                        @click="$emit('cancel')"
                      >
                        {{ cancelLabel }}
                      </ZoneButton>
                    </component>

                    <ZoneButton
                      v-if="confirmLabel"
                      :color="resolvedConfirmColor"
                      data-testid="modal-confirm"
                      @click="onConfirm"
                    >
                      {{ confirmLabel }}
                    </ZoneButton>
                  </slot>
                </div>
              </slot>
            </div>
          </component>
        </div>
      </DialogOverlay>
    </DialogPortal>
  </component>
</template>

<script lang="ts">
import { computed, ref, type Component, type CSSProperties, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogRoot,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from 'reka-ui';
import ZoneButton, { type ButtonColor } from './Button.vue';
import ZoneIcon, { type IconColor, type IconName, type IconShade } from './Icon.vue';
import ZoneIconButton from './IconButton.vue';

export type ModalSize = 'sm' | 'md' | 'lg';
export type ModalTone = 'destructive' | 'warning' | 'informative' | 'neutral';
export type ModalScroll = 'content' | 'modal';

const SIZES: ModalSize[] = ['sm', 'md', 'lg'];
const TONES: ModalTone[] = ['destructive', 'warning', 'informative', 'neutral'];
const SCROLLS: ModalScroll[] = ['content', 'modal'];

// Figma's Small/Medium/Large frames (410 / 670 / 1200). They are MAX widths — the modal is w-full
// underneath, so a narrower screen simply gets a narrower modal, which is what the spec's sizes
// note describes ("as the screen gets less wide they resize their width").
const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-[410px]',
  md: 'max-w-[670px]',
  lg: 'max-w-[1200px]',
};

// The Figma icon shape: a 32px circle in the tone's 100 shade, holding an 18px unfilled glyph.
const TONE_SHAPE_CLASSES: Record<ModalTone, string> = {
  destructive: 'bg-red-100',
  warning: 'bg-yellow-100',
  informative: 'bg-primary-100',
  neutral: 'bg-gray-100',
};

const TONE_ICONS: Record<ModalTone, { name: IconName; color: IconColor; shade: IconShade }> = {
  destructive: { name: 'warning', color: 'red', shade: 600 },
  warning: { name: 'warning', color: 'yellow', shade: 500 },
  informative: { name: 'info', color: 'primary', shade: 600 },
  neutral: { name: 'info', color: 'gray', shade: 500 },
};

// Figma's "Styling colors" section: the primary CTA carries the modal's tone.
const TONE_CONFIRM_COLORS: Record<ModalTone, ButtonColor> = {
  destructive: 'red',
  warning: 'yellow',
  informative: 'primary',
  neutral: 'gray',
};

// No z-index beyond the z-50 every teleported Zone overlay uses, so paint order inside
// #zone-teleport follows mount order — see the NOTES block above.
const OVERLAY_CLASSES = 'fixed inset-0 z-50 overflow-y-auto bg-gray-500/40';

const CONTENT_CLASSES =
  'relative flex w-full flex-col rounded-lg border border-gray-200 bg-white shadow-xl focus:outline-none';

// 64px = the p-8 gutter the positioner keeps on either side at md and up.
const CONTENT_SCROLL_CLASSES = 'max-h-[calc(100vh-64px)]';

// Figma header: 20px sides, 20 above / 10 below the 26px title line.
const HEADER_CLASSES = 'flex items-center gap-4 px-5 pb-2.5 pt-5';

const TITLE_CLASSES = 'min-w-0 flex-1 truncate text-lg font-semibold text-gray-900';

const BODY_CLASSES = 'flex flex-col gap-5 px-5 py-2.5';
const BODY_SCROLL_CLASSES = 'min-h-0 flex-1 overflow-y-auto';

// Figma footer: tertiary on the left, cancel + confirm on the right; stacked on mobile with the
// PRIMARY on top — hence flex-col-reverse against a cancel-then-confirm source order. Same
// arrangement as Zone::Modal::Actions, expressed without that component's row-reverse.
const FOOTER_CLASSES = 'flex flex-col-reverse gap-3 px-5 pb-5 pt-2.5 md:flex-row md:items-center md:gap-5';
const ACTION_GROUP_CLASSES =
  'flex flex-col-reverse gap-3 md:flex-row md:items-center md:gap-5 [&>*]:w-full md:[&>*]:w-auto';
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneModal' });

const { t } = useI18n();

const props = defineProps({
  /**
   * Open state, as v-model. Left unset (null) the modal owns its own state; pass it — even as
   * `false` — and it becomes fully controlled, so the trigger only emits and the caller decides.
   * `default: null` rather than `false` is what keeps Vue from boolean-casting an absent prop.
   */
  modelValue: {
    type: Boolean,
    default: null,
  },
  /** Initial open state while uncontrolled. Ignored once `modelValue` is passed. */
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  /** Header text; truncates to one line, per Figma. */
  title: {
    type: String,
    default: null,
  },
  /** The message under the header, wired to `aria-describedby`. */
  description: {
    type: String,
    default: null,
  },
  /** Accessible name used when there is no visible title. */
  ariaLabel: {
    type: String,
    default: null,
  },
  /**
   * Back the modal with Reka's AlertDialog instead of its Dialog: `role="alertdialog"`, and focus
   * lands on the cancel button rather than the first focusable element. Use it for a decision the
   * user must actually make (delete, disable, discard).
   */
  alert: {
    type: Boolean,
    default: false,
  },
  /**
   * Draws Figma's round icon shape beside the description and colours the confirm button: red /
   * yellow / primary / gray.
   */
  tone: {
    type: String as PropType<ModalTone>,
    default: null,
    validator: (value: string) => TONES.includes(value as ModalTone),
  },
  /**
   * Overrides the tone's own icon (warning for destructive and warning, info for informative and
   * neutral).
   */
  icon: {
    type: String as PropType<IconName>,
    default: null,
  },
  /**
   * 410 / 670 / 1200px max widths. The modal is `w-full` below that, so a narrow screen just gets a
   * narrower modal.
   */
  size: {
    type: String as PropType<ModalSize>,
    default: 'md',
    validator: (value: string) => SIZES.includes(value as ModalSize),
  },
  /**
   * A CSS max-width for the modal (e.g. `'480px'`) instead of `size`, applied inline so a dynamic
   * value is not dropped by the Tailwind JIT (same reasoning as Zone::Modal#modal_style).
   */
  width: {
    type: String,
    default: null,
  },
  /** The × in the header. */
  closeButton: {
    type: Boolean,
    default: true,
  },
  /**
   * Escape closes it; `false` blocks that. The × and the buttons still work, and an outside click
   * never closes it (see the docs).
   */
  dismissible: {
    type: Boolean,
    default: true,
  },
  /**
   * `content` caps the modal height and scrolls the body, leaving the header and footer pinned
   * (what Figma draws). `modal` lets the whole modal grow and scroll inside the viewport instead.
   */
  scroll: {
    type: String as PropType<ModalScroll>,
    default: 'content',
    validator: (value: string) => SCROLLS.includes(value as ModalScroll),
  },
  /** Renders the primary button, coloured by `tone`. */
  confirmLabel: {
    type: String,
    default: null,
  },
  /** Renders the outline/gray_alt button. */
  cancelLabel: {
    type: String,
    default: null,
  },
  /** A ZoneButton colour that overrides the tone-derived one. */
  confirmColor: {
    type: String as PropType<ButtonColor>,
    default: null,
  },
  /**
   * Set `false` to keep the modal open while an async confirm runs, then close it yourself through
   * v-model.
   */
  closeOnConfirm: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  /** The new open state. */
  'update:modelValue': [open: boolean];
  /** It opened (Zone::Modal's modal:opened). */
  open: [];
  /** It closed (Zone::Modal's modal:closed). */
  close: [];
  /** The confirm button was clicked; fires before the close. */
  confirm: [];
  /** The cancel button was clicked. */
  cancel: [];
}>();

const slots = defineSlots<{
  /**
   * The element that opens it. Rendered with Reka's `as-child`, so it must have exactly one root element (a
   * ZoneButton, a `<button>`…). Omit it entirely and drive the modal with v-model.
   */
  trigger?(props: { open: boolean }): any;
  /** The body. */
  default?(props: { close: () => void }): any;
  /** Replaces the `title` prop's text; the `<h2>` and its styling stay. */
  title?(): any;
  /** Replaces the `description` prop's text. */
  description?(): any;
  /** Replaces the confirm/cancel buttons with your own, in the same right-hand group. */
  actions?(props: { close: () => void }): any;
  /** The left-hand footer action (Figma's ghost "Button text"). */
  tertiary?(props: { close: () => void }): any;
  /** Replaces the whole action row, keeping only its padding. */
  footer?(props: { close: () => void }): any;
}>();

const uncontrolledOpen = ref(props.defaultOpen);

const isControlled = computed<boolean>(() => props.modelValue !== null);

const isOpen = computed<boolean>(() => (isControlled.value ? props.modelValue : uncontrolledOpen.value));

const rootComponent = computed<Component>(() => (props.alert ? AlertDialogRoot : DialogRoot));

/**
 * Only Root, Content and Cancel actually differ between the two families — AlertDialogTrigger,
 * Portal, Overlay, Title and Description are thin re-exports of the Dialog ones, and
 * AlertDialogRoot renders a DialogRoot, so they all share one context.
 */
const contentComponent = computed<Component>(() => (props.alert ? AlertDialogContent : DialogContent));

const cancelComponent = computed<Component>(() => (props.alert ? AlertDialogCancel : DialogClose));

const hasVisibleTitle = computed<boolean>(() => Boolean(props.title || slots.title));

const ariaLabel = computed<string>(() => props.ariaLabel ?? t('aria_label'));

const titleText = computed<string>(() => (hasVisibleTitle.value ? props.title : ariaLabel.value));

const hasDescription = computed<boolean>(() => Boolean(props.description || slots.description));

const hasHeader = computed<boolean>(() => hasVisibleTitle.value || props.closeButton);

const hasActions = computed<boolean>(() => Boolean(slots.actions || props.confirmLabel || props.cancelLabel));

const hasFooter = computed<boolean>(() => Boolean(slots.footer || slots.tertiary || hasActions.value));

const contentScroll = computed<boolean>(() => props.scroll === 'content');

const contentClasses = computed<string>(() => {
  const classes = [CONTENT_CLASSES];

  if (!props.width) {
    classes.push(SIZE_CLASSES[props.size]);
  }

  if (contentScroll.value) {
    classes.push(CONTENT_SCROLL_CLASSES);
  }

  return classes.join(' ');
});

const contentStyle = computed<CSSProperties>(() => (props.width ? { maxWidth: props.width } : {}));

/**
 * Reka always writes `aria-describedby` from its own generated id, then warns to the console
 * when nothing carries that id. Overriding it with `undefined` is the documented opt-out and
 * leaves the attribute off the element entirely.
 */
const contentAttrs = computed<Record<string, unknown>>(() => (hasDescription.value ? {} : { 'aria-describedby': undefined }));

const titleClasses = computed<string>(() => (hasVisibleTitle.value ? TITLE_CLASSES : 'sr-only'));

const bodyClasses = computed<string>(() => (contentScroll.value ? `${BODY_CLASSES} ${BODY_SCROLL_CLASSES}` : BODY_CLASSES));

const toneIcon = computed<{ name: IconName; color: IconColor; shade: IconShade }>(() => (
  TONE_ICONS[props.tone] ?? TONE_ICONS.neutral
));

const resolvedIcon = computed<IconName>(() => props.icon || toneIcon.value.name);

const iconShapeClasses = computed<string>(() => {
  const shape = props.tone ? TONE_SHAPE_CLASSES[props.tone] : '';

  return `flex size-8 shrink-0 items-center justify-center rounded-full ${shape}`;
});

const resolvedConfirmColor = computed<ButtonColor>(() => {
  if (props.confirmColor) {
    return props.confirmColor;
  }

  return props.tone ? TONE_CONFIRM_COLORS[props.tone] : 'primary';
});

/**
 * The Root is always given a real boolean for `open`, so it runs controlled and this is the
 * single place the state can change.
 */
function onOpenChange(value: boolean) {
  if (value === isOpen.value) {
    return;
  }

  if (!isControlled.value) {
    uncontrolledOpen.value = value;
  }

  emit('update:modelValue', value);

  if (value) {
    emit('open');
  } else {
    emit('close');
  }
}

/**
 * Reka's DismissableLayer only dismisses when the event it emitted was not default-prevented,
 * so this is how `dismissible: false` swallows Escape without also disabling the × or the footer
 * buttons (which close through DialogClose, not this layer). Outside clicks are always prevented
 * in the template.
 */
function onEscape(event: Event) {
  if (!props.dismissible) {
    event.preventDefault();
  }
}

function close() {
  onOpenChange(false);
}

function onConfirm() {
  emit('confirm');

  if (props.closeOnConfirm) {
    close();
  }
}
</script>

<i18n lang="json">
{
  "en": {
    "aria_label": "Dialog",
    "close": "Close"
  }
}
</i18n>
